interface DecimalJsonError {
  title: string;
  detail: string;
  errorCode?: string;
}

interface OffsetPaginationFields {
  totals: { items: number; pageItems: number; pages: number; results: number };
  pages: {
    current: number;
    first: number;
    last: number;
  };
  offsets: {
    current: number;
    first: number;
    last: number;
    prev?: number;
    next?: number;
  };
}

interface CursorPaginationFields {
  totals: { pageItems: number };
  cursors?: { current: string; first: string; next?: string };
}

type PaginationFields = {
  size: number;
  type: "offset" | "cursor";
} & OffsetPaginationFields &
  CursorPaginationFields;

/**
 * Raw data coming from decimal
 */
interface DecimalJsonResponse<T> {
  data?: T;
  errors?: DecimalJsonError;
  meta?: {
    page?: PaginationFields;
  };
  links?: {
    first: string;
    last: string;
    next?: string;
    prev?: string;
  };
}

/**
 * Massaged decimal data returned to caller
 */
export interface DecimalResult<T> {
  ok: boolean;
  data?: T;
  error?: string;
  meta: DecimalJsonResponse<T>["meta"];
  status?: number;
  links?: DecimalJsonResponse<T>["links"];
  errorCode?: string;
}

export interface ErrorOptions {
  throw401s?: boolean;
}

/**
 * You might find it helpful to extract the type of `data` returned from
 * a decimal call.
 */
export type DecimalResultData<T extends () => Promise<{ data?: unknown }>> =
  Awaited<ReturnType<T>>["data"];

/**
 * Wrapper for DecimalResult that asserts the data field is defined
 */
export type DecimalRequiredData<DR extends DecimalResult<unknown>> = Omit<
  DR,
  "data"
> &
  Required<Pick<DR, "data">>;

export function buildDecimalUrl(path: string): URL {
  const decimalPath =
    (import.meta.env.VITE_DECIMAL_PATH as string) || "/decimal";
  path = decimalPath + path;
  return new URL(window.location.origin + path);
}

export function callDecimal<T>(
  path: string,
  requestOptions: RequestInit = {},
  errorOptions: ErrorOptions = {
    throw401s: true,
  }
): Promise<DecimalResult<T>> {
  const url = buildDecimalUrl(path);

  requestOptions = {
    ...requestOptions,
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json; charset=UTF-8",
      "X-Requested-With": "XMLHttpRequest",
      ...requestOptions.headers,
    },
  };

  return fetch(url, requestOptions)
    .then(wrapResponse<T>)
    .catch((err: unknown) => createResponseError<T>(errorString(err)))
    .then((decimalResult: DecimalResult<T>) => {
      // This is facilitating automatic bubbling of errors so that loaders
      // are not required to handle them
      if (errorOptions.throw401s && decimalResult.status === 401) {
        throw new Response("Unauthorized", { status: 401 });
      }
      return decimalResult;
    });
}

const errorString = (error: unknown): string => {
  let message = "Unknown Error";
  if (error instanceof Error) {
    message = error.message;
  } else if (error instanceof String) {
    message = error as string;
  }
  return message;
};

const wrapResponse = async <T>(
  response: Response
): Promise<DecimalResult<T>> => {
  try {
    const data: DecimalJsonResponse<T> = (
      response.status == 204 ? {} : await response.json()
    ) as DecimalJsonResponse<T>;
    if (response.ok) {
      return createResponseSuccess<T>(
        data.data,
        response.status,
        data.meta,
        data.links
      );
    } else {
      return createResponseError<T>(
        data.errors?.detail,
        response.status,
        {},
        data.errors?.errorCode
      );
    }
  } catch (error) {
    return createResponseError<T>(errorString(error), response.status);
  }
};

const createResponseSuccess = <T>(
  data: T | undefined,
  status: number,
  meta = {},
  links: DecimalJsonResponse<T>["links"]
): DecimalResult<T> => ({
  ok: true,
  data,
  meta,
  status,
  links,
});

const createResponseError = <T>(
  error: string | undefined,
  status: number | undefined = undefined,
  meta = {},
  errorCode?: string | undefined
): DecimalResult<T> => ({
  ok: false,
  error,
  meta,
  status,
  errorCode,
});
